import { AppError } from '@cetused/shared'

/**
 * The SHELL boot — everything the app needs to reach React render and show the
 * PUBLIC login route. Deliberately tiny.
 *
 * ⚠ WHY THIS NO LONGER RUNS THE DATA STACK — read before you "restore" it.
 *
 * This ladder USED to run eight steps inline: mode detection, then the keyring,
 * the encrypted storage gateway, state hydration, RBAC, lanes, and the audit
 * trail. Steps 2–6 were once no-op stubs that resolved having done nothing, so
 * the ladder resolved and the app rendered a WORKSPACE over no key, no database,
 * no state, and every lane inactive — a fiction (TASK-00066 turned those stubs
 * into honest refusals). The refusals then did the opposite harm: bootSequence()
 * rejected at the storage step, main.tsx surfaced it, and the app rendered
 * NOTHING — so a user could not even reach the login form. Pre-auth UI was held
 * hostage by the post-auth DATA stack it does not use.
 *
 * The fix is not a try/catch that swallows the refusal (an error is not
 * permission to boot — ⛔2, and a rendered-workspace-over-hollow-stubs is the
 * exact fiction above). The fix is architectural: the login page and the app
 * shell depend ONLY on the API client and the session store, never on the
 * keyring / encrypted storage / state / RBAC / lanes / audit. Those are the
 * DATA stack; they exist for STUDENT DATA and are lazy-initialized on first
 * entry into an authenticated data route (see ./data-stack.ts, ensureDataStack).
 *
 * FAIL-CLOSED IS PRESERVED, NOT WEAKENED. The data stack still REFUSES today —
 * it just refuses when a data route actually uses it, where the route shows an
 * honest "not available yet" and renders no student data (ui/data/DataStackBoundary).
 * Boot tolerance for the shell must never leak into "data works without the
 * gateway": do not import ensureDataStack here, and do not add a data step back
 * into this shell boot.
 */
export async function bootSequence(): Promise<void> {
  // Step 1: Detect deployment mode — an unrecognized mode is a build/config
  // defect; boot stops (fail closed) rather than guessing a deployment shape.
  const mode = import.meta.env.VITE_DEPLOY_MODE || 'self-hosted'
  if (mode !== 'vendor-hosted' && mode !== 'self-hosted') {
    throw AppError.internal(`Unknown deployment mode: ${mode}`)
  }

  // Step 2: Probe API connectivity so an offline shell can say so. Non-fatal by
  // design — the login form still renders offline and the API is reached when
  // the user actually submits; a probe failure is warned, never thrown.
  const { apiClient } = await import('@api/client')
  try {
    await apiClient.health.$get()
  } catch (err) {
    console.warn('[CetusEd] API server not reachable — running in offline mode', err)
  }
}
